// ============================================================
// CineSync — Firebase & Cloud Firestore Initialization
// ============================================================

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';

const REQUIRED_FIREBASE_ENV_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

/**
 * Validates that all six required VITE_FIREBASE_* environment variables are present and populated.
 * Throws a descriptive error message if any variable is missing.
 */
function validateFirebaseEnv(): void {
  const missing = REQUIRED_FIREBASE_ENV_KEYS.filter((key) => {
    const val = import.meta.env[key];
    return !val || typeof val !== 'string' || val.trim().length === 0;
  });

  if (missing.length > 0) {
    throw new Error(
      `[Firebase] Missing required environment variable(s): ${missing.join(', ')}. ` +
      'Please ensure all VITE_FIREBASE_* variables are correctly defined in your environment (.env.local).'
    );
  }
}

// Perform validation on module import
validateFirebaseEnv();

/**
 * Firebase Web Client configuration read directly from environment variables.
 * Values are not hardcoded in source code.
 */
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase App instance (preventing duplicate initialization on fast reload)
export const app: FirebaseApp =
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

// Initialize Firebase Authentication instance
export const auth: Auth = getAuth(app);

// Enforce browser local persistence across tabs and browser restarts
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.warn('[Firebase] setPersistence error:', err);
});

// Google Authentication Provider instance
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Initialize Cloud Firestore instance
export const db: Firestore = getFirestore(app);

export default app;

